<template>
  <v-row>
    <v-col cols="12">
      <v-card class="pur-card" flat>
        <!-- HEADER BAND -->
        <div class="pur-header pl-base-bg">
          <div class="pur-header-icon">
            <v-icon color="#fff" size="22">mdi-cart-outline</v-icon>
          </div>
          <div>
            <div class="pur-title">Edit Purchase</div>
            <div class="pur-subtitle">{{ form.billNumber ? `Bill #${form.billNumber}` : "Update purchase details" }}</div>
          </div>
          <v-spacer />
          <v-btn
            v-if="status !== 'PAID' && status !== 'CANCELLED' && !isViewOnly"
            small
            class="pur-pay-btn mr-2"
            @click="openPaymentDialog"
          >
            Mark as Paid
          </v-btn>
          <v-chip :color="statusColor(status)" dark small class="text-uppercase font-weight-bold">
            {{ status }}
          </v-chip>
        </div>

        <v-form ref="form" v-model="valid" class="pa-6" :disabled="isPaid">
          <!-- VENDOR + BILL DETAILS -->
          <div class="pur-section-label">Bill Details</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-autocomplete
                v-model="selectedVendor"
                :items="vendors"
                item-text="name"
                return-object
                label="Vendor"
                outlined
                dense
                :disabled="isPaid"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-domain"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.billNumber"
                label="Vendor Bill Number"
                outlined
                dense
                :disabled="isPaid"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-file-document-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.billDate"
                    label="Bill Date"
                    outlined
                    dense
                    readonly
                    :disabled="isPaid"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.billDate" @input="dateMenu = false" />
              </v-menu>
            </v-col>
          </v-row>

          <!-- ITEMS -->
          <div class="pur-section-label mt-4">Items</div>

          <div
            v-for="(item, index) in form.items"
            :key="index"
            class="pur-item-row"
          >
            <div class="pur-item-row-head">
              <span class="pur-item-index">Item {{ index + 1 }}</span>
              <v-btn icon x-small class="pur-item-remove" :disabled="isPaid" @click="removeItem(index)">
                <v-icon color="error" size="20">mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="item.description"
                  label="Description"
                  dense
                  outlined
                  hide-details
                  :disabled="isPaid"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="6" sm="2">
                <v-text-field v-model="item.hsn" label="HSN/SAC" dense outlined hide-details :disabled="isPaid" />
              </v-col>

              <v-col cols="4" sm="1">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Qty"
                  dense
                  outlined
                  hide-details
                  :disabled="isPaid"
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.price"
                  type="number"
                  label="Rate"
                  dense
                  outlined
                  hide-details
                  :disabled="isPaid"
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.taxPercent"
                  type="number"
                  label="Tax %"
                  dense
                  outlined
                  hide-details
                  suffix="%"
                  :disabled="isPaid"
                  @input="calcItem(index)"
                />
              </v-col>
            </v-row>

            <div class="pur-item-row-foot">
              <div class="pur-item-amount">
                <span class="pur-item-amount-label">Amount</span>
                <span class="pur-item-amount-value">{{ formatAmount(item.amount) }}</span>
              </div>

              <div
                class="pur-item-itc"
                :class="{ 'pur-item-itc--off': !item.isItcEligible, 'pur-item-itc--locked': isPaid }"
              >
                <v-switch
                  v-model="item.isItcEligible"
                  dense
                  inset
                  hide-details
                  :disabled="isPaid"
                  class="pur-item-itc-switch"
                />
                <span>ITC Eligible</span>
              </div>
            </div>
          </div>

          <v-btn small class="pur-add-item-btn" :disabled="isPaid" @click="addItem">
            <v-icon left size="16">mdi-plus</v-icon>
            Add Item
          </v-btn>

          <!-- TOTALS -->
          <v-divider class="my-4" />
          <v-row dense justify="end">
            <v-col cols="12" sm="4">
              <div class="pur-total-row">
                <span>Subtotal</span>
                <span>{{ formatAmount(subtotal) }}</span>
              </div>
              <div class="pur-total-row">
                <span>GST</span>
                <span>{{ formatAmount(totalGst) }}</span>
              </div>
              <div class="pur-total-row pur-total-row--grand">
                <span>Total</span>
                <span>{{ formatAmount(subtotal + totalGst) }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- BILL IMAGE -->
          <div class="pur-section-label mt-4">Bill / Receipt</div>
          <div v-if="document && !removeExisting" class="pur-existing-doc">
            <a :href="document.fileUrl" target="_blank">{{ document.fileName || "View attached file" }}</a>
            <v-btn icon small :disabled="isPaid" @click="removeExisting = true">
              <v-icon size="18" color="error">mdi-close</v-icon>
            </v-btn>
          </div>
          <v-file-input
            v-else
            v-model="uploadedFile"
            label="Attach a photo or PDF of the vendor bill"
            outlined
            dense
            :disabled="isPaid"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            accept="image/*,application/pdf"
          />

          <!-- NOTE -->
          <v-textarea v-model="form.note" label="Note" outlined dense rows="2" :disabled="isPaid" />

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block color="error" outlined :disabled="isPaid" @click="confirmDelete = true">
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="fetchPurchase">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block class="pur-save-btn" :loading="loading" :disabled="isPaid" @click="submit">
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this purchase?</p>
    </global-dialog>

    <!-- PAYMENT DIALOG -->
    <global-dialog
      v-if="paymentDialog"
      v-model="paymentDialog"
      :title="`Pay Vendor - ${form.billNumber}`"
      :buttons="paymentDialogButtons"
    >
      <v-row dense>
        <v-col cols="12" class="mt-3">
          <v-alert type="info" dense text>
            You can enter a partial amount to record a partial payment. The remaining balance will stay as due.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="paymentAmount"
            label="Amount"
            type="number"
            outlined
            dense
            :hint="`Balance due: ${formatAmount(remainingBalance)}`"
            persistent-hint
            :error-messages="
              paymentAmount && !validPaymentAmount
                ? `Amount must be between 1 and ${formatAmount(remainingBalance)}`
                : ''
            "
          />

          <v-select
            v-model="paymentMethod"
            :items="paymentMethods"
            label="Payment Method"
            outlined
            dense
            required
            class="mt-4"
          />
        </v-col>
      </v-row>
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "@/components/common/global-dialog.vue";

export default {
  name: "EditPurchase",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      dateMenu: false,
      confirmDelete: false,
      selectedVendor: null,
      uploadedFile: null,
      document: null,
      removeExisting: false,
      status: "PENDING",
      totalAmount: 0,
      amountPaid: 0,

      paymentDialog: false,
      paymentMethod: "",
      paymentAmount: 0,
      paymentMethods: [
        "CASH",
        "BANK_TRANSFER",
        "UPI",
        "DEBIT_CARD",
        "CREDIT_CARD",
        "CHEQUE",
        "NET_BANKING",
        "WALLET",
        "OTHER",
      ],

      form: {
        billNumber: "",
        billDate: "",
        reverseCharge: false,
        note: "",
        items: [],
      },

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  created() {
    this.$store.dispatch("vendors/getVendors");
    this.fetchPurchase();
  },

  computed: {
    vendors() {
      return this.$store.state.vendors.vendors;
    },

    isPaid() {
      return this.status === "PAID";
    },

    subtotal() {
      return this.form.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    },

    totalGst() {
      return this.form.items.reduce((sum, it) => {
        const amount = Number(it.amount) || 0;
        const percent = Number(it.taxPercent) || 0;
        return sum + (amount * percent) / 100;
      }, 0);
    },

    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        { text: "Delete", action: this.delete, color: "error" },
      ];
    },

    remainingBalance() {
      return (this.totalAmount || 0) - (this.amountPaid || 0);
    },

    validPaymentAmount() {
      const v = parseFloat(this.paymentAmount);
      return !isNaN(v) && v > 0 && v <= this.remainingBalance;
    },

    paymentDialogButtons() {
      return [
        {
          text: "Cancel",
          color: "secondary",
          action: this.closePaymentDialog,
        },
        {
          text: "Confirm",
          color: "primary",
          disabled: !this.paymentMethod || !this.validPaymentAmount,
          action: this.confirmPayment,
        },
      ];
    },
  },

  methods: {
    ...mapActions("purchases", [
      "getPurchaseById",
      "updatePurchase",
      "deletePurchase",
      "markPurchaseAsPaid",
    ]),

    async fetchPurchase() {
      try {
        const id = this.$route.params.id;
        const res = await this.getPurchaseById(id);
        const p = res.purchase || res;

        this.status = p.status || "PENDING";
        this.totalAmount = p.totalAmount || 0;
        this.amountPaid = p.amountPaid || 0;
        this.document = res.document || null;
        this.removeExisting = false;
        this.uploadedFile = null;

        this.selectedVendor = p.vendor?.vendorId
          ? { _id: p.vendor.vendorId, name: p.vendor.name, gstin: p.vendor.gstin, address: p.vendor.address, state: p.vendor.state }
          : null;

        this.form = {
          billNumber: p.billNumber || "",
          billDate: p.billDate ? new Date(p.billDate).toISOString().slice(0, 10) : "",
          reverseCharge: !!p.reverseCharge,
          note: p.note || "",
          items: (p.items || []).map((it) => ({ ...it })),
        };
      } catch (err) {
        this.$toast.error("Failed to load purchase");
      }
    },

    addItem() {
      this.form.items.push({
        description: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
        isItcEligible: true,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      item.amount = +(qty * price).toFixed(2);
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    statusColor(status) {
      switch (status) {
        case "PAID":
          return "green";
        case "PARTIALLY_PAID":
          return "blue";
        case "CANCELLED":
          return "red";
        default:
          return "orange";
      }
    },

    readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      if (!this.selectedVendor) {
        this.$toast?.error("Please select a vendor");
        return;
      }

      this.loading = true;

      try {
        const id = this.$route.params.id;

        const payload = {
          id,
          billNumber: this.form.billNumber,
          billDate: this.form.billDate,
          reverseCharge: this.form.reverseCharge,
          note: this.form.note,
          vendor: {
            vendorId: this.selectedVendor._id,
            name: this.selectedVendor.name,
            gstin: this.selectedVendor.gstin,
            address: this.selectedVendor.address,
            state: this.selectedVendor.state,
          },
          items: this.form.items,
          existingDocumentId: this.document?._id,
          removeFile: this.removeExisting,
        };

        if (this.uploadedFile) {
          payload.file = await this.readFileAsBase64(this.uploadedFile);
          payload.fileName = this.uploadedFile.name;
          payload.fileType = this.uploadedFile.type;
        }

        await this.updatePurchase(payload);

        this.$toast.success("Purchase updated successfully");
        this.$router.push("/purchases");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Failed to update purchase",
        );
      } finally {
        this.loading = false;
      }
    },

    async delete() {
      const id = this.$route.params.id;

      try {
        await this.deletePurchase(id);

        this.$toast?.success("Purchase deleted successfully");
        this.$router.push("/purchases");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete purchase",
        );
      } finally {
        this.confirmDelete = false;
      }
    },

    openPaymentDialog() {
      this.paymentMethod = "";
      this.paymentAmount = this.remainingBalance;
      this.paymentDialog = true;
    },

    closePaymentDialog() {
      this.paymentDialog = false;
      this.paymentMethod = "";
      this.paymentAmount = 0;
    },

    async confirmPayment() {
      try {
        const res = await this.markPurchaseAsPaid({
          id: this.$route.params.id,
          paymentMethod: this.paymentMethod,
          amount: parseFloat(this.paymentAmount),
        });

        const updated = res?.purchase;
        if (updated) {
          this.status = updated.status;
          this.amountPaid = updated.amountPaid;
        }

        this.$toast?.success(res?.message || "Payment recorded");
        this.closePaymentDialog();
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update status",
        );
      }
    },
  },
};
</script>

<style scoped>
.pur-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.pur-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.pur-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pur-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.pur-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.pur-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.pur-item-row {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
}

.pur-item-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.pur-item-index {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.pur-item-remove {
  margin: -4px -4px 0 0;
}

.pur-item-row-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.pur-item-amount {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.pur-item-amount-label {
  font-size: 12px;
  color: #64748b;
}

.pur-item-amount-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.pur-item-itc {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
  background: #e6f7ec;
  border-radius: 100px;
  padding: 4px 12px 4px 4px;
  transition: background 0.15s, color 0.15s;
}

.pur-item-itc--off {
  color: #94a3b8;
  background: #eef1f5;
}

.pur-item-itc--locked {
  opacity: 0.6;
}

.pur-item-itc-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.pur-item-itc-switch >>> .v-input__control,
.pur-item-itc-switch >>> .v-input__slot {
  margin: 0 !important;
}

.pur-item-itc-switch >>> .v-messages {
  display: none;
}

.pur-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.pur-total-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 0;
  color: #475569;
}

.pur-total-row--grand {
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  margin-top: 4px;
  padding-top: 8px;
}

.pur-existing-doc {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 10px 14px;
  margin-bottom: 16px;
}

.pur-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.pur-pay-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
